import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'catalog/v1/restaurants';
export const getCatalogItemsEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    name: TParam;
    sku: TParam;
    price: TParam;
    unitId: TParam;
    categoryId: TParam;
    isActive: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

function constructEndpoint(restaurantId: string, warehouseId: string) {
  return `${endpoint}/${restaurantId}/warehouses/${warehouseId}/items`;
}

export function useGetCatalogItemsApi(restaurantId: string, warehouseId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name, sort, sku, price, unitId, categoryId, isActive } = getPageParams();
  const fullEndpoint = constructEndpoint(restaurantId, warehouseId);

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: { page, limit, sort, name, sku, price, unitId, categoryId, isActive },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [fullEndpoint, { page, limit, sort, name, sku, price, unitId, categoryId, isActive }],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
